import type { ProductCatalog, ProductInformation, LegacyPlanId } from '@hotjar/product-catalog';
import type { ProductType, Product } from '@hotjar/webapp/domains/common/product/types';
import { ProductCatalogRepository, getV3SkuFromPlanId } from '@hotjar/product-catalog';
import type { ProductAllowance } from '~/subscription-management/api/common/model/subscription';

export type SelectedPlans = {
  [siteId: string]: {
    [productType in ProductType.Feedback | ProductType.Analytics]: Product | null | undefined;
  };
};

export type Site = {
  id: number;
  name: string;
};

/**
 * Maps a ProductInformation to a ProductAllowance (to be added to the draft order), which will be easily parsed
 * by the checkout page.
 */
export function mapProductToProductAllowance(
  product: ProductInformation | undefined,
  site: Site | undefined,
): ProductAllowance | undefined {
  if (!product || !site) {
    return undefined;
  }

  return {
    sku: product.allowance.sku,
    entity: {
      entityIdentifier: `${site.id}`,
      entityLabel: site.name ?? '',
      entityType: 'site',
    },
  };
}

/**
 * Maps a list of plans with legacy skus to a list of product allowances in v3 (draft order products)
 */
export function mapSelectedPlansToV3ProductAllowances({
  selectedPlans,
  productCatalog,
  sites,
}: {
  selectedPlans: SelectedPlans;
  productCatalog: ProductCatalog | undefined;
  sites: Site[];
}): ProductAllowance[] {
  const allowances: ProductAllowance[] = [];

  if (!productCatalog) {
    return allowances;
  }

  const repo = new ProductCatalogRepository(productCatalog);

  Object.entries(selectedPlans).forEach(([siteId, plans]) => {
    const site = sites.find((siteObj) => `${siteObj.id}` === siteId);

    Object.values(plans).forEach((product) => {
      if (!product) {
        return;
      }

      const v3Product = repo.getProductBySku(getV3SkuFromPlanId(product.sku as LegacyPlanId));
      const productAllowance = mapProductToProductAllowance(v3Product, site);

      if (productAllowance) {
        allowances.push(productAllowance);
      }
    });
  });

  return allowances;
}
